import type { ReactNode } from "react"

import { CloseButton } from "@/components/ui/close-button"
import { CheckIcon, SparkleIcon } from "@/components/ui/icons"
import { Text } from "@/components/ui/text"
import { tv, type VariantProps } from "@/lib/utils/tv"

const iconWrap = tv({
  base: "flex size-8 shrink-0 items-center justify-center rounded-full",
  variants: {
    tone: {
      success: "bg-surface-success-subtle text-fg-success-deep",
      info: "bg-brand-subtle text-fg-brand",
      error: "bg-error-fill text-error-text",
    },
  },
  defaultVariants: { tone: "info" },
})

interface ToastProps extends VariantProps<typeof iconWrap> {
  title: ReactNode
  body?: ReactNode
  onClose?: () => void
}

export const Toast = ({ tone = "info", title, body, onClose }: ToastProps) => (
  <div
    role={tone === "error" ? "alert" : "status"}
    aria-live={tone === "error" ? "assertive" : "polite"}
    className="rounded-6x border-line-subtle bg-surface-raised shadow-floating flex max-w-90 items-start gap-300 border p-400"
  >
    <span className={iconWrap({ tone })}>{tone === "error" ? <span className="font-bold">!</span> : tone === "success" ? <CheckIcon size={16} /> : <SparkleIcon size={14} />}</span>
    <div className="flex-1">
      <Text as="div" variant="body" family="display" tone="default" className="font-semibold">
        {title}
      </Text>
      {body && (
        <Text as="div" variant="bodySm" tone="subtle" className="mt-50">
          {body}
        </Text>
      )}
    </div>
    {onClose && <CloseButton onClick={onClose} />}
  </div>
)
